/**
 * IL MAGAZZINO DELLE PREFERENZE DELLA PERSONA — il contratto.
 *
 * Il provider sta in `PreferenzeProvider.tsx`; qui non ci sono componenti, perche' un hook
 * esportato da un file di componenti spegne il fast-refresh su tutto quel file. E' la stessa
 * separazione di `detailSlotContext.ts` / `DetailSlot.tsx`.
 *
 * ## Perche' un magazzino e non una lettura per chiave
 *
 * Fino al 16-09-2026 `usePreferenzaUtente` faceva **una query per chiave, per ogni componente
 * che la chiedeva**: un `GET /api/user-preferences` filtrato con `page.size:1` a testa. Solo la
 * cornice del telefono ne faceva quattro all'avvio (`barShape`, `barAutoHide`, `barItems`,
 * `detailSurface`), e ognuna arrivava per conto suo — quindi ogni pezzo dell'interfaccia
 * saltava dal predefinito al valore vero in un momento diverso.
 *
 * Adesso e' **una chiamata sola**, all'avvio, per tutte. Il server filtra gia' per persona e
 * azienda dal token, quindi «tutte» vuol dire davvero tutte le sue.
 *
 * ## E perche' questo ha permesso di togliere lo specchio locale
 *
 * Con una lettura sola c'e' un unico momento in cui «le preferenze sono arrivate», quindi si
 * puo' **aspettarlo** — e chi aspetta non ha bisogno di un valore di scorta in `localStorage`
 * per non far vedere il salto. Le 18 chiavi della persona non hanno piu' una copia sul
 * dispositivo: vivono sul server e basta. Restano locali le 21 cose che sul server non possono
 * stare — il token, la coda delle timbrature, il blocco dell'app — che sono locali per
 * costruzione e non per comodita'.
 */
import { createContext, useContext } from 'react';

/** Una riga di `user-preferences`, ridotta a cio' che serve: dove sta e cosa contiene. */
export interface PreferenceEntry {
  /** L'id della riga sul server: serve per aggiornarla invece di crearne una seconda. */
  id: string;
  /** Il valore **grezzo**, cioe' il JSON come il server lo tiene. L'interpretazione e' di chi legge. */
  text: string;
}

export interface PreferencesStore {
  /**
   * La prima lettura si e' assestata.
   *
   * ⚠️ E' `true` anche quando non c'e' nessuno da leggere (nessuna sessione) e quando l'attesa
   * e' scaduta: vuol dire «non aspettare altro», non «il server ha risposto». Chi lo usa per
   * decidere se disegnare deve poter andare avanti comunque — un cancello che non si apre mai
   * e' peggio di un valore predefinito.
   */
  ready: boolean;
  /** Il valore grezzo di una chiave, o `undefined` se il server non ne ha uno. */
  read: (key: string) => string | undefined;
  /**
   * Scrive sul server e aggiorna subito il magazzino, senza aspettare la risposta.
   *
   * La promessa dice **com'e' andata**, e serve a un caso solo ma importante: la promozione di
   * un vecchio valore locale, che puo' cancellare la copia sul dispositivo soltanto dopo che il
   * server ha confermato. Non rifiuta mai — un `false` e' un esito, non un'eccezione da
   * raccogliere in ogni punto di chiamata.
   */
  write: (chiave: string, text: string) => Promise<boolean>;
}

export const PreferencesContext = createContext<PreferencesStore | null>(null);

/**
 * Il magazzino, o `null` fuori dal provider.
 *
 * ⚠️ Torna `null` invece di sollevare, e la ragione e' che il provider avvolge l'applicazione
 * ma **non** i casi di prova che montano un componente da solo. Chi lo riceve `null` deve
 * comportarsi come se non ci fosse niente salvato — predefiniti, e scritture che non vanno da
 * nessuna parte — non come se ci fosse un guasto.
 *
 * Che nell'applicazione vera ci sia davvero non e' affidato alla buona volonta':
 * `tests/unit/preferenzeUnicaLettura.test.tsx` fallisce se `AppProviders` smette di montarlo.
 */
export function usePreferences(): PreferencesStore | null {
  return useContext(PreferencesContext);
}
